import React, { useState, useEffect, useMemo, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { addJuceListener, removeJuceListener, callNative, isJuceEnvironment } from './juceInterop';
import './setup.css';

const SearchIcon = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>;
const PackageIcon = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="16.5" y1="9.4" x2="7.5" y2="4.21"></line><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path><polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline><line x1="12" y1="22.08" x2="12" y2="12"></line></svg>;
const BoxIcon = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path><polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline><line x1="12" y1="22.08" x2="12" y2="12"></line></svg>;
const RefreshIcon = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="spin-anim"><polyline points="23 4 23 10 17 10"></polyline><polyline points="1 20 1 14 7 14"></polyline><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path></svg>;

export default function SetupPluginManager({ allOwnedProducts = [] }) {
    const [plugins, setPlugins] = useState([]);
    const [isScanning, setIsScanning] = useState(false);
    const [scanProgress, setScanProgress] = useState({ current: 0, total: 0, name: '' });
    const [searchTerm, setSearchTerm] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const hasInitialised = useRef(false);

    useEffect(() => {
        const scanFinishedId = addJuceListener('scanFinished', () => {
            console.log('SCAN FINISHED');
            setIsScanning(false);
            setScanProgress({ current: 0, total: 0, name: '' });
            fetchPluginList();
        });

        const scanProgressId = addJuceListener('scanProgress', (event) => {
            const data = event.detail !== undefined ? event.detail : event;
            console.log('SCAN PROGRESS EVENT:', data);
            setIsScanning(true);
            setScanProgress({ current: data.current || 0, total: data.total || 0, name: data.pluginName || '' });

            // Re-render live using the bridged array directly, bypassing the heavy XML reads
            if (data.plugins && Array.isArray(data.plugins)) {
                setPlugins(data.plugins);
            }
        });

        fetchPluginList();

        return () => {
            removeJuceListener(scanFinishedId);
            removeJuceListener(scanProgressId);
        };
    }, []);

    const fetchPluginList = async (isProgressive = false) => {
        const list = await callNative('getPluginList', []);
        console.log('GET PLUGIN LIST:', list);
        const loadedPlugins = list || [];
        setPlugins(loadedPlugins);

        if (!isProgressive) {
            // Just mark fetch as complete
        }
    };

    useEffect(() => {
        if (!hasInitialised.current && allOwnedProducts.length > 0) {
            hasInitialised.current = true;
            const hasValidLicense = allOwnedProducts.some(p =>
                p.name && (p.name.toLowerCase() === 'refract' || p.name.toLowerCase() === 'duck')
            );

            if (plugins.length === 0 && isJuceEnvironment() && hasValidLicense) {
                startScan();
            }
        }
    }, [allOwnedProducts, plugins]);
    const startScan = async () => {
        setIsScanning(true);
        await callNative('triggerScan', []);
    };

    const filteredPlugins = useMemo(() => {
        if (!searchTerm) return plugins;
        const lower = searchTerm.toLowerCase();
        return plugins.filter(p =>
            p.name.toLowerCase().includes(lower) ||
            (p.manufacturer && p.manufacturer.toLowerCase().includes(lower)) ||
            (p.pluginFormatName && p.pluginFormatName.toLowerCase().includes(lower))
        );
    }, [plugins, searchTerm]);

    const groupedPlugins = useMemo(() => {
        const map = new Map();
        filteredPlugins.forEach(p => {
            const format = p.pluginFormatName || 'Other';
            if (!map.has(format)) map.set(format, []);
            map.get(format).push(p);
        });
        return map;
    }, [filteredPlugins]);

    if (!isOpen) {
        return (
            <div style={{ marginTop: '20px', width: '100%', marginBottom: '60px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '15px' }}>
                <button
                    onClick={() => setIsOpen(true)}
                    className="setup-btn-secondary"
                    style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 24px' }}
                >
                    <PackageIcon />
                    Scanned Plugins ({plugins.length})
                    {isScanning && <RefreshIcon />}
                </button>
                {isScanning && scanProgress.name && (
                    <div style={{ fontSize: '11px', color: '#666', maxWidth: '300px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {scanProgress.name}
                    </div>
                )}
                <style>{`
                    .spin-anim {
                        animation: spin 1.5s linear infinite;
                    }
                    @keyframes spin {
                        100% { transform: rotate(360deg); }
                    }
                `}</style>
            </div>
        );
    }

    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0,0,0,0.85)',
                        backdropFilter: 'blur(10px)',
                        zIndex: 1000,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        padding: '20px'
                    }}
                >
                    <motion.div
                        initial={{ y: 50, opacity: 0, scale: 0.95 }}
                        animate={{ y: 0, opacity: 1, scale: 1 }}
                        exit={{ y: 50, opacity: 0, scale: 0.95 }}
                        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                        style={{
                            width: '100%',
                            maxWidth: '700px',
                            maxHeight: '85vh',
                            backgroundColor: '#1a1a1a',
                            borderRadius: '16px',
                            border: '1px solid rgba(255,255,255,0.1)',
                            display: 'flex',
                            flexDirection: 'column',
                            overflow: 'hidden',
                            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)'
                        }}
                    >
                        <div style={{
                            padding: '20px 24px',
                            borderBottom: '1px solid rgba(255,255,255,0.05)',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            backgroundColor: 'rgba(255,255,255,0.02)'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <div style={{
                                    width: '36px', height: '36px',
                                    borderRadius: '8px',
                                    backgroundColor: 'rgba(255,255,255,0.05)',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    color: '#fff'
                                }}>
                                    <PackageIcon />
                                </div>
                                <div>
                                    <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: '#fff' }}>
                                        Plugin Manager
                                    </h2>
                                    <p style={{ margin: 0, fontSize: '13px', color: '#888', marginTop: '2px' }}>
                                        {plugins.length} Plugins Scanned
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={() => setIsOpen(false)}
                                style={{
                                    background: 'rgba(255,255,255,0.05)',
                                    border: 'none',
                                    width: '32px',
                                    height: '32px',
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: '#fff',
                                    cursor: 'pointer',
                                    opacity: 0.5,
                                    marginLeft: 'auto'
                                }}
                                onMouseEnter={e => e.currentTarget.style.opacity = '1'}
                                onMouseLeave={e => e.currentTarget.style.opacity = '0.5'}
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                            </button>
                        </div>

                        <div style={{ padding: '20px 24px', display: 'flex', gap: '12px' }}>
                            <div style={{
                                flex: 1,
                                position: 'relative',
                                display: 'flex',
                                alignItems: 'center'
                            }}>
                                <div style={{ position: 'absolute', left: '12px', color: '#666', pointerEvents: 'none', display: 'flex' }}>
                                    <SearchIcon />
                                </div>
                                <input
                                    type="text"
                                    placeholder="Search plugins by name, format, or manufacturer..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    style={{
                                        width: '100%',
                                        padding: '12px 12px 12px 40px',
                                        backgroundColor: 'rgba(0,0,0,0.3)',
                                        border: '1px solid rgba(255,255,255,0.1)',
                                        borderRadius: '8px',
                                        color: '#fff',
                                        fontSize: '14px',
                                        outline: 'none',
                                        transition: 'border-color 0.2s'
                                    }}
                                    onFocus={(e) => e.target.style.borderColor = 'rgba(255,255,255,0.3)'}
                                    onBlur={(e) => e.target.style.borderColor = 'rgba(255,255,255,0.1)'}
                                />
                            </div>

                            <button
                                onClick={async () => {
                                    setIsScanning(true);
                                    await callNative('clearPlugins', []);
                                    // Manually force the UI array to empty so the user receives immediate visual feedback
                                    setPlugins([]);
                                    setIsScanning(false);
                                }}
                                disabled={isScanning}
                                className="setup-btn-secondary"
                                style={{
                                    padding: '0 20px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '8px',
                                    opacity: isScanning ? 0.7 : 1,
                                    backgroundColor: 'transparent',
                                    boxShadow: 'none',
                                    color: '#ff4444'
                                }}
                            >
                                Clear Known Plugins
                            </button>

                            <button
                                onClick={startScan}
                                disabled={isScanning}
                                className={isScanning ? "setup-btn-secondary" : "setup-btn-primary"}
                                style={{
                                    padding: '0 20px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '8px',
                                    minWidth: '140px',
                                    opacity: isScanning ? 0.7 : 1
                                }}
                            >
                                {isScanning ? (
                                    <>
                                        <RefreshIcon />
                                        Scanning...
                                    </>
                                ) : (
                                    <>
                                        <RefreshIcon className="" />
                                        Force Scan
                                    </>
                                )}
                            </button>
                        </div>

                        <div style={{ flex: 1, overflowY: 'auto', padding: '0 24px 24px 24px' }}>
                            {isScanning && (
                                <motion.div
                                    initial={{ opacity: 0, height: 0 }}
                                    animate={{ opacity: 1, height: 'auto' }}
                                    className="setup-scan-progress"
                                    style={{
                                        backgroundColor: 'rgba(0,0,0,0.2)',
                                        border: '1px solid rgba(255,255,255,0.05)',
                                        borderRadius: '8px',
                                        padding: '16px',
                                        marginBottom: '20px'
                                    }}
                                >
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '13px', color: '#aaa' }}>
                                        <span>Scanning Native Environment...</span>
                                        <span>{scanProgress.current} scanned</span>
                                    </div>
                                    <div style={{ color: '#fff', fontSize: '12px', opacity: 0.5, wordBreak: 'break-all', fontFamily: 'monospace' }}>
                                        {scanProgress.name || 'Initializing search paths...'}
                                    </div>
                                </motion.div>
                            )}

                            {plugins.length === 0 && !isScanning ? (
                                <div style={{
                                    textAlign: 'center',
                                    padding: '60px 20px',
                                    color: '#666',
                                    border: '1px dashed rgba(255,255,255,0.1)',
                                    borderRadius: '12px',
                                    marginTop: '10px'
                                }}>
                                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: '16px', opacity: 0.5 }}><line x1="16.5" y1="9.4" x2="7.5" y2="4.21"></line><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path><polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline><line x1="12" y1="22.08" x2="12" y2="12"></line></svg>
                                    <p style={{ margin: '0 0 8px 0', fontSize: '16px', color: '#fff' }}>No plugins scanned yet.</p>
                                    <p style={{ margin: 0, fontSize: '14px', maxWidth: '300px', margin: '0 auto' }}>Click Force Scan to build the directory index for Tape Signal.</p>
                                </div>
                            ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                                    {[...groupedPlugins.keys()].sort().map((format) => (
                                        <div key={format}>
                                            <h3 style={{
                                                fontSize: '11px',
                                                textTransform: 'uppercase',
                                                letterSpacing: '1px',
                                                color: '#666',
                                                borderBottom: '1px solid rgba(255,255,255,0.05)',
                                                paddingBottom: '8px',
                                                marginBottom: '12px'
                                            }}>
                                                {format} Plugins ({groupedPlugins.get(format).length})
                                            </h3>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                                {groupedPlugins.get(format).sort((a, b) => a.name.localeCompare(b.name)).map((plugin, idx) => (
                                                    <div key={idx} style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        padding: '12px 16px',
                                                        backgroundColor: 'rgba(255,255,255,0.02)',
                                                        border: '1px solid rgba(255,255,255,0.05)',
                                                        borderRadius: '8px',
                                                        transition: 'background-color 0.2s'
                                                    }}
                                                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.04)'}
                                                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.02)'}
                                                    >
                                                        <div style={{ marginRight: '16px', color: '#666' }}>
                                                            <BoxIcon />
                                                        </div>
                                                        <div style={{ flex: 1, overflow: 'hidden' }}>
                                                            <div style={{ color: '#fff', fontSize: '14px', fontWeight: '500', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{plugin.name}</div>
                                                            <div style={{ color: '#666', fontSize: '12px', marginTop: '2px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{plugin.manufacturer || 'Unknown Manufacturer'}</div>
                                                        </div>
                                                        <div style={{
                                                            fontSize: '11px',
                                                            padding: '4px 8px',
                                                            backgroundColor: 'rgba(255,255,255,0.05)',
                                                            borderRadius: '4px',
                                                            color: '#888'
                                                        }}>
                                                            {plugin.pluginFormatName}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
